import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';

import { assertObject } from '@app/core/utilities/object.utilities';
import { ForcedFilter } from '@app/shared/components';
import { TranslateService } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';
import { FilterMode } from '../../models/selector.model';
import { BaseSelectorService } from '../../services/base-selector.service';
import { getButtonLabel, getSelectorTooltip } from '../../services/global-filters.utils';

@Component({
  standalone: false,
  selector: 'adspt-base-global-selector',
  template: '',
  providers: [BaseSelectorService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseGlobalSelectorComponent implements OnInit {
  filterMode = FilterMode;
  selectedTabIndex = 0;
  isVisible = false;

  @Input() disableReset = false; // disable reset icon button
  @Input() hasDestinationSelector = false; // multiple selections
  @Input() enableAdvancedMode = false;
  @Input() enableAdvancedFilterSelection = false;
  @Input() hideFloatingLabel = false;
  @Input() set forcedFilter(forcedFilter: ForcedFilter) {
    this.service.forcedFilters$.next(assertObject(forcedFilter));
  }

  @Output() selectionChange = new EventEmitter();
  @Output() reset = new EventEmitter();

  readonly subfilterOptions = this.service.subfilterOptions();
  readonly srcTotalCount$ = this.service.srcCount$.asObservable();
  readonly destTotalCount$ = this.service.destCount$.asObservable();
  readonly srcList$ = this.service.srcItems$;
  readonly destList$ = this.service.destItems$;
  readonly loader$ = this.service.loader$;
  readonly listLoader$ = this.service.listLoader$;
  readonly displayData$ = this.service.data$.pipe(
    startWith([{ type: 'LABEL' }, { type: 'LABEL' }]),
    map((d) => {
      const [src, dest, filters] = d as any;
      const returnValue = {
        src: src,
        dest: dest,
        defaultSubfilters: filters || [],
        multpleSelections: this.hasDestinationSelector && dest.type !== 'LABEL' && src.type !== 'LABEL',
        labels: {
          tooltip: getSelectorTooltip([src, dest], this.hasDestinationSelector),
          buttonTexts: getButtonLabel([src, dest], this.hasDestinationSelector),
        },
        selectedItems: {
          [FilterMode.Source]: src?.type === 'SELECTED_ITEM' && src?.payload?.id,
          [FilterMode.Destination]: !this.hasDestinationSelector
            ? false
            : dest?.type === 'SELECTED_ITEM' && dest?.payload?.id,
        },
      };
      if (!this.hasDestinationSelector) delete returnValue.dest;

      return returnValue;
    }),
  );

  onDropDownVisibiltiyChange(value: boolean) {
    if (!value) this.resetToInital();
  }

  closeDialogOrSwitchTabs() {
    if (this.hasDestinationSelector) {
      if (this.selectedTabIndex === 0)
        this.selectedTabIndex = 1; //switch to next tab
      else this.resetToInital(); // close dialog
    } else this.resetToInital();

    this.cd.markForCheck();
  }

  resetToInital() {
    this.isVisible = false;
    this.selectedTabIndex = 0;
    this.service.clearCache();
  }

  onNotifications(value, tab: FilterMode) {
    switch (value?.type) {
      case 'ITEM_CLICK':
        this.selectionChange.emit({ type: 'ID', payload: value.payload, tab });
        this.closeDialogOrSwitchTabs();
        break;
      case 'APPLY':
        if (this.service.filterDefaults[this.service.defaultSubfiltersType] === value.payload) this.reset.emit({ tab });
        else this.selectionChange.emit({ type: 'FILTER', payload: value.payload, tab });

        this.closeDialogOrSwitchTabs();
        break;
      case 'RESET':
        this.reset.emit({ tab });
        this.closeDialogOrSwitchTabs();
        break;
      case 'SCROLL':
        if (this.listLoader$.value) return;
        const func = tab === FilterMode.Source ? 'onSrcListScrolled$' : 'onDestListScrolled$';
        this.service[func].next(this.service[func].value + 1);
        break;
    }
  }

  onRefinerChange({ filters, query }, tab: FilterMode) {
    const func = tab === FilterMode.Source ? 'onSrcListScrolled$' : 'onDestListScrolled$';
    this.service[func].next(0);
    this.service[tab === FilterMode.Source ? 'srcSub$' : 'destSub$'].next({
      filters: filters,
      query: query,
    });
  }

  ngOnInit(): void {
    this.service.hasDestinationSelector = this.hasDestinationSelector;
  }
  constructor(
    protected service: BaseSelectorService,
    protected translate: TranslateService,
    protected cd: ChangeDetectorRef,
  ) {}
}
